import React from "react";
import { Box, Typography } from "@mui/material";
import { DialogComponent } from "../../atom";
import TextButtonComponet from "../../atom/Buttons/TextButton";
import {
  ASSOCIATION_ACTION,
  ASSOCIATION_CATEGORY,
  renderTruncatedText,
} from "../../../core/Constant";

const formatLabel = (value) =>
  String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const getAssociationItems = (data) => {
  const associations = data?.associate_items || data;
  if (Array.isArray(associations)) {
    return { items: associations, folders: [], courseContents: [] };
  }
  if (!associations || typeof associations !== "object") {
    return { items: [associations], folders: [], courseContents: [] };
  }

  const folders = [];
  const courseContents = [];
  const items = Object.entries(associations).flatMap(([category, values]) => {
    const isCourseContent =
      category === ASSOCIATION_CATEGORY.COURSE_CONTENT ||
      category === "courseContents";

    if (
      category === ASSOCIATION_CATEGORY.CHILDREN ||
      category === ASSOCIATION_CATEGORY.IS_ASSOCIATED
    ) return [];

    if (Array.isArray(values)) {
      if (isCourseContent) {
        courseContents.push(...values.map((value) => getItemLabel(value)));
        return [];
      }
      return values.map((value) => `${formatLabel(category)}: ${getItemLabel(value)}`);
    }

    if (values && typeof values === "object") {
      if (category === ASSOCIATION_CATEGORY.ASSOCIATE_FOLDERS) {
        folders.push(...Object.values(values).map((folderName) => getItemLabel(folderName)));
        return [];
      }

      if (isCourseContent) {
        courseContents.push(
          ...Object.entries(values).map(
            ([sectionName, courseName]) => `${getItemLabel(courseName)} > ${sectionName}`
          )
        );
        return [];
      }

      return Object.entries(values).map(
        ([value, parent]) =>
          `${formatLabel(category)}: ${formatLabel(value)} > ${getItemLabel(parent)}`
      );
    }

    if (isCourseContent) {
      courseContents.push(getItemLabel(values));
      return [];
    }

    return [`${formatLabel(category)}: ${getItemLabel(values)}`];
  });

  return { items, folders, courseContents };
};

const getItemLabel = (item) => {
  if (item && typeof item === "object") {
    return item.name || item.title || item.label || item.id || JSON.stringify(item);
  }
  return String(item);
};

const DocumentAssociationDialog = ({ association, onClose, onConfirm }) => {
  if (!association) return null;

  const { items, folders, courseContents } = getAssociationItems(association.data);
  const action =
    association.action === ASSOCIATION_ACTION.DELETE
      ? ASSOCIATION_ACTION.DELETE
      : ASSOCIATION_ACTION.EDIT;
  const entityType = association.entityType || "document";
  const entityName = association.entityName || association.documentName;

  return (
    <DialogComponent
      isShowCloseButton={true}
      title={`${entityType.toUpperCase()} ASSOCIATION`}
      open={true}
      maxwidth="sm"
      onClose={onClose}
    >
      <Box sx={{ px: 3, pb: 3 }}>
        <Typography sx={{ color: "#2D3945", fontSize: 16, lineHeight: 1.5, mt: 2 }}>
          The <strong>{renderTruncatedText(entityName, { limit: 40 })}</strong> is currently assigned in the
          following {items.length === 1 ? "item" : "items"}.
           <br/>If you {action} this{" "}
          {entityType}, it will affect all listed items :<br/>
        </Typography><br/>
        <Box
          component="ul"
          sx={{
            color: "#2D3945",
            pl: 3,
            mb: 2,
            ...(items.length + folders.length + courseContents.length > 7 && {
              maxHeight: 230,
              overflowY: "auto",
            }),
          }}
        >
          {items.map((item, index) => (
            <li key={`${getItemLabel(item)}-${index}`}>
              <Typography>{getItemLabel(item)}</Typography>
            </li>
          ))}
          {folders.length > 0 && (
            <li>
              <Typography component="span" sx={{ fontWeight: 600, fontSize: "18px" }}>
                Associated folder:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                {folders.map((folder, index) => (
                  <li key={`${folder}-${index}`}>
                    <Typography>{folder}</Typography>
                  </li>
                ))}
              </Box>
            </li>
          )}
          {courseContents.length > 0 && (
            <li>
              <Typography component="span" sx={{ fontWeight: 600, fontSize: "18px" }}>
                Associated course content:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                {courseContents.map((courseContent, index) => (
                  <li key={`${courseContent}-${index}`}>
                    <Typography>{courseContent}</Typography>
                  </li>
                ))}
              </Box>
            </li>
          )}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <TextButtonComponet
            text="Cancel"
            classStyle="btn btn-secondary"
            onButtonClick={onClose}
          />
          <TextButtonComponet text="Continue" onButtonClick={onConfirm} />
        </Box>
      </Box>
    </DialogComponent>
  );
};

export default DocumentAssociationDialog;